import React, { useEffect, useRef, useState } from 'react';
import {
  inspectionsApi,
  ComponentComment,
  ComponentCommentKind,
  RoomComponent,
} from '@/api/inspections';
import { cn } from '@/ui';

// Inspection-side comments section (notes + limitations) for a single
// component. Two internal sub-components (CommentSubsection,
// InspectionCommentRow) live in this file because they only render
// inside ComponentCommentsSection. The "Add note" / "Add limitation"
// trigger buttons used to live here too — they were lifted into the
// unified AddItemRow inside ComponentList.tsx so all four add
// affordances (Photo / Finding / Note / Limitation) sit together.

interface ComponentCommentsSectionProps {
  inspectionId: string;
  component: RoomComponent;
  readOnly: boolean;
  onCommentChanged: (componentId: string, comment: ComponentComment) => void;
  onCommentDeleted: (componentId: string, commentId: string) => void;
}

// Renders the existing notes + limitations lists for a component. The
// "Add note" / "Add limitation" trigger buttons used to live here too;
// they were lifted into the unified add-item row inside ComponentRow so
// every "+ Photo / + Finding / + Note / + Limitation" affordance sits in
// the same place.
export const ComponentCommentsSection: React.FC<ComponentCommentsSectionProps> = ({
  inspectionId,
  component,
  readOnly,
  onCommentChanged,
  onCommentDeleted,
}) => {
  const notes = component.comments.filter((c) => c.kind === 'note');
  const limitations = component.comments.filter((c) => c.kind === 'limitation');

  if (component.comments.length === 0) return null;

  return (
    <div className="space-y-2">
      {notes.length > 0 && (
        <CommentSubsection
          inspectionId={inspectionId}
          component={component}
          kind="note"
          items={notes}
          readOnly={readOnly}
          onCommentChanged={onCommentChanged}
          onCommentDeleted={onCommentDeleted}
        />
      )}
      {limitations.length > 0 && (
        <CommentSubsection
          inspectionId={inspectionId}
          component={component}
          kind="limitation"
          items={limitations}
          readOnly={readOnly}
          onCommentChanged={onCommentChanged}
          onCommentDeleted={onCommentDeleted}
        />
      )}
    </div>
  );
};

interface CommentSubsectionProps {
  inspectionId: string;
  component: RoomComponent;
  kind: ComponentCommentKind;
  items: ComponentComment[];
  readOnly: boolean;
  onCommentChanged: (componentId: string, comment: ComponentComment) => void;
  onCommentDeleted: (componentId: string, commentId: string) => void;
}

const CommentSubsection: React.FC<CommentSubsectionProps> = ({
  inspectionId,
  component,
  kind,
  items,
  readOnly,
  onCommentChanged,
  onCommentDeleted,
}) => {
  const label = kind === 'note' ? 'Notes' : 'Limitations';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between px-0.5">
        <span
          className={cn(
            'text-overline uppercase tracking-wide',
            kind === 'limitation' ? 'text-amber-700' : 'text-ink-subtle',
          )}
        >
          {label}
        </span>
      </div>
      <div
        className={cn(
          'rounded-lg border divide-y bg-surface-1',
          kind === 'limitation'
            ? 'border-amber-300 divide-amber-200'
            : 'border-border-subtle divide-border-subtle',
        )}
      >
        {items.map((cm) => (
          <InspectionCommentRow
            key={cm.id}
            inspectionId={inspectionId}
            componentId={component.id}
            comment={cm}
            readOnly={readOnly}
            onCommentChanged={onCommentChanged}
            onCommentDeleted={onCommentDeleted}
          />
        ))}
      </div>
    </div>
  );
};

interface InspectionCommentRowProps {
  inspectionId: string;
  componentId: string;
  comment: ComponentComment;
  readOnly: boolean;
  onCommentChanged: (componentId: string, comment: ComponentComment) => void;
  onCommentDeleted: (componentId: string, commentId: string) => void;
}

const InspectionCommentRow: React.FC<InspectionCommentRowProps> = ({
  inspectionId,
  componentId,
  comment,
  readOnly,
  onCommentChanged,
  onCommentDeleted,
}) => {
  const [draft, setDraft] = useState(comment.body);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Keep the draft in sync if the server pushes a new body (e.g. after refresh).
  useEffect(() => {
    if (!editing) setDraft(comment.body);
  }, [comment.body, editing]);

  // Auto-grow the textarea while editing.
  useEffect(() => {
    if (!editing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, editing]);

  const commit = async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setDraft(comment.body);
      setEditing(false);
      return;
    }
    if (trimmed === comment.body) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const res = await inspectionsApi.updateComponentComment(
        inspectionId,
        componentId,
        comment.id,
        { body: trimmed },
      );
      if (res.success && res.data) {
        onCommentChanged(componentId, res.data);
      }
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const toggleIncluded = async () => {
    if (readOnly || toggling) return;
    setToggling(true);
    try {
      const res = await inspectionsApi.updateComponentComment(
        inspectionId,
        componentId,
        comment.id,
        { is_included: !comment.isIncluded },
      );
      if (res.success && res.data) {
        onCommentChanged(componentId, res.data);
      }
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = async () => {
    const res = await inspectionsApi.deleteComponentComment(
      inspectionId,
      componentId,
      comment.id,
    );
    if (res.success) {
      onCommentDeleted(componentId, comment.id);
    }
  };

  const isLimitation = comment.kind === 'limitation';

  return (
    <div
      className={cn(
        'flex items-start gap-2 px-3 py-2',
        !comment.isIncluded && 'opacity-55',
        isLimitation && 'bg-amber-50/50',
      )}
    >
      {!readOnly && (
        <label
          className="flex items-center justify-center pt-1 cursor-pointer shrink-0"
          title={comment.isIncluded ? 'Included in report' : 'Excluded from report'}
        >
          <input
            type="checkbox"
            checked={comment.isIncluded}
            onChange={toggleIncluded}
            disabled={toggling}
            className="w-4 h-4 accent-brand cursor-pointer"
            aria-label={`Include this ${comment.kind} in the report`}
          />
        </label>
      )}
      {isLimitation && (
        <svg
          className="w-4 h-4 text-amber-600 shrink-0 mt-1"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v4M12 17h.01M10.3 3.9 2.4 17a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"
          />
        </svg>
      )}
      <div className="flex-1 min-w-0">
        {editing && !readOnly ? (
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setDraft(comment.body);
                setEditing(false);
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                (e.target as HTMLTextAreaElement).blur();
              }
            }}
            disabled={saving}
            rows={1}
            className={cn(
              'w-full text-body-sm text-ink bg-transparent resize-none outline-none',
              'border border-brand/40 rounded px-2 py-1 focus:border-brand',
              saving && 'opacity-60',
            )}
            autoFocus
          />
        ) : (
          <button
            type="button"
            onClick={() => !readOnly && setEditing(true)}
            className={cn(
              'w-full text-left text-body-sm text-ink whitespace-pre-wrap break-words',
              // Excluded state is already conveyed by the unchecked box +
              // opacity-55 dim on the row. Adding line-through on top reads as
              // "scratched out / wrong" — the opposite of "opt-in to include".
              readOnly ? 'cursor-default' : 'cursor-text hover:bg-surface-2/60 rounded px-1 -mx-1',
            )}
          >
            {comment.body}
          </button>
        )}
        {comment.source === 'custom' && (
          <span className="inline-block mt-1 text-caption text-ink-subtle">Custom</span>
        )}
      </div>
      {!readOnly && (
        <button
          type="button"
          onClick={handleDelete}
          className="p-1 text-ink-subtle hover:text-safety-solid rounded shrink-0"
          aria-label={`Delete ${comment.kind}`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

